/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconDots from '@assets/icons/dots.svg?react';
import {
 Avatar,
 Box,
 Button,
 CircularProgress,
 IconButton,
 Menu,
 MenuItem,
 TextField,
 Theme,
 Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useAuthContext } from '@/contexts/AuthContext';
import { useConfirmationDialog } from '../../../contexts/useConfirmationDialog';
import {
 addCommentStart,
 CommentsType,
 deleteCommentStart,
 getCommentsStart,
 updateCommentStart,
} from '../../../redux/comments/commentsSlice';
import { RootState } from '../../../redux/store';
import { titleStyles } from '../../common/InfoSidebar/infoStyles';
import UserAvatar from '@/components/common/Header/UserAvatar';

const CommentInput = ({
 value,
 setValue,
}: {
 value: string;
 setValue: (value: string) => void;
}) => {
 const { t } = useTranslation();
 return (
  <TextField
   id="comment-input"
   multiline
   minRows={2}
   placeholder={t('gridSidebar.whiteComment')}
   sx={{
    '& .MuiInputBase-root': {
     p: '12px',
    },
    '& fieldset': { border: 'none' },
    width: '100%',
    background: (theme: Theme) => theme.palette.kemek.lightBlue,
    '& textarea': {
     fontSize: 12,
     pb: 5,
    },
   }}
   value={value}
   onChange={(e) => setValue(e.target.value)}
  />
 );
};

const InfoComments = ({ rowId }: { rowId: number }) => {
 const dispatch = useDispatch();
 const { t } = useTranslation();
 const { hasRole } = useAuthContext();
 const { openDialog: openConfirmationDialog } = useConfirmationDialog();
 const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
 const commentsSelector = useSelector((state: RootState) => state.comments);
 const { comments, loading, success } = commentsSelector;
 const [comment, setComment] = useState('');
 const [currentId, setCurrentId] = useState<number | undefined>(undefined);
 const [currentComment, setCurrentComment] = useState<CommentsType | undefined>(
  undefined,
 );
 const canCreate = hasRole('Comments', 'Create');
 const canDelete = hasRole('Comments', 'Delete');
 const canUpdate = hasRole('Comments', 'Update');
 const canGetAll = hasRole('Comments', 'GetAll');
 const canReadComments = canGetAll;

 const getComments = () => {
  if (!canReadComments) return;

  dispatch(
   getCommentsStart({
    id: rowId,
    table: 'Result',
   }),
  );
 };

 useEffect(() => {
  if (!rowId || !canReadComments) return;
  getComments();
 }, [dispatch, rowId, canReadComments]);

 useEffect(() => {
  if (success?.addComment || success?.updateComment || success?.deleteComment) {
   getComments();
   setComment('');
   setCurrentId(undefined);
   setCurrentComment(undefined);
  }
 }, [success]);

 const handleSubmit = () => {
  const trimmedComment = comment.trim();
  if (!trimmedComment) return;

  if (currentId) {
   if (!canUpdate) return;

   dispatch(
    updateCommentStart({
     id: currentId,
     Text: trimmedComment,
    }),
   );
  } else {
   if (!canCreate) return;

   dispatch(
    addCommentStart({
     Table: 'Result',
     TableRecordId: rowId,
     Text: trimmedComment,
    }),
   );
  }
 };

 const handleDelete = (id: number) => {
  if (!canDelete) return;

  openConfirmationDialog({
   title: t('global.delete'),
   message: t('global.deleteMessage'),
   onConfirm: () => {
    dispatch(deleteCommentStart({ id }));
   },
  });
 };

 if (!canGetAll) return null;

 return (
  <>
   <Typography sx={titleStyles}>Komentarai</Typography>
   <Box
    sx={{
     display: 'flex',
     gap: 2,
     flexDirection: 'column',
     mb: 3,
    }}
   >
    {loading?.getComments ? (
     <CircularProgress />
    ) : !comments ? (
     'Šiuo metu komentarų nėra.'
    ) : (
     comments?.map((c: CommentsType) => (
      <Box
       key={`comment_${c.Id}`}
       sx={{
        background: (theme: Theme) =>
         c.Id === currentId ? theme.palette.kemek.lightBlue : 'transparent',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
       }}
      >
       <Box
        sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}
       >
        <UserAvatar
         user={{
          // fix it
          Name: c.UserFullName.split(' ')[0],
          Surname: c.UserFullName.split(' ')[1],
         }}
         size={40}
        />
        <Box>
         <Typography sx={{ fontSize: 12 }}>{c.UserFullName}</Typography>
         <Typography
          sx={{
           fontSize: 10,
           color: (theme: Theme) => theme.palette.kemek.gray,
          }}
         >
          {c.Modified
           ? dayjs(c.Modified).format('YYYY-MM-DD')
           : dayjs(c.Created).format('YYYY-MM-DD')}
         </Typography>
        </Box>
        {(canUpdate || canDelete) && (
         <Box sx={{ ml: 'auto' }}>
          <IconButton
           title={t('global.menu')}
           aria-label={t('global.menu')}
           color="primary"
           onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(e.currentTarget);
            setCurrentComment(c);
           }}
          >
           <IconDots />
          </IconButton>
          <Menu
           MenuListProps={{ disablePadding: true }}
           anchorEl={anchorEl}
           open={!!anchorEl}
           onClose={() => {
            setAnchorEl(null);
            setCurrentComment(undefined);
           }}
          >
           {canUpdate && (
            <MenuItem
             onClick={() => {
              setComment(currentComment?.Text || '');
              setCurrentId(currentComment?.Id);
              setAnchorEl(null);
             }}
            >
             {t('global.edit')}
            </MenuItem>
           )}
           {canDelete && (
            <MenuItem
             sx={{
              color: (theme: Theme) =>
               theme.palette.mode === 'dark'
                ? theme.palette.kemek.lightRed
                : theme.palette.kemek.red,
             }}
             onClick={() => {
              if (currentComment) {
               handleDelete(currentComment?.Id);
               setAnchorEl(null);
              }
             }}
            >
             {t('global.delete')}
            </MenuItem>
           )}
          </Menu>
         </Box>
        )}
       </Box>
       <Typography sx={{ fontSize: 12 }}>{c.Text}</Typography>
      </Box>
     ))
    )}
   </Box>
   {(canCreate || currentId) && (
    <Box sx={{ position: 'relative' }}>
     <CommentInput value={comment} setValue={setComment} />
     <Box
      sx={{
       display: 'flex',
       gap: 1,
       position: 'absolute',
       bottom: '12px',
       right: '12px',
      }}
     >
      {currentId && (
       <Button
        variant="text"
        size="small"
        color="secondary"
        type="button"
        onClick={() => {
         setComment('');
         setCurrentId(undefined);
        }}
       >
        {t('global.cancel')}
       </Button>
      )}
      <Button
       variant="contained"
       type="button"
       size="small"
       sx={{ height: '34px !important' }}
       onClick={() => handleSubmit()}
       disabled={!comment.trim() || (currentId ? !canUpdate : !canCreate)}
      >
       {t('global.send')}
      </Button>
     </Box>
    </Box>
   )}
  </>
 );
};

export default InfoComments;

